import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openClickToDial } from "../../data/user/actions";

import {
  selectUserId,
  selectUserProfile,
  selectIsClickToDialOpen,
  selectLeadAutomator,
  selectCallSettings,
  selectClickToDialContactPropertyDetails,
  selectClickToDialData,
  selectCallSettingsLoading,
  selectIsLeadAutomatorLoading,
  selectAlert,
  selectCallCountForToday,
  selectCallCountForTodayLoading,
} from "data/user/selectors";

import {
  getLeadAutomatorAPI,
  getCallSettingIntegration,
  getClickToDialContactPropertyDetailsById,
  getUserProfile,
  clickToDialIsLoaded,
  saveCallLog,
  getAlertByName,
  disableAlert,
  getCallCountForToday,
} from "data/user/actions";

import { DailyCallLimit_NiceWorkAlert_Name, DailyCallLimit_Notification_CallCountThreshold } from "data/user/constants";

import UpgradeToLeadAutomator from "./UpgradeToLeadAutomator/UpgradeToLeadAutomator";
import MicrophoneNotDetected from "./MicrophoneNotDetected/MicrophoneNotDetected";
import FullAccessRequiredModal from "./FullAccessRequiredModal/FullAccessRequiredModal";
import ReadyToCall from "../../components/ReadytoCall/ReadyToCall";
import readytocallscss from "../../components/ReadytoCall/ReadyToCall.module.scss";
import css from "../../app/AccountNew/style.scss";
import SetupPhoneNumber from "../../app/AccountNew/modals/SetupPhoneNumber";
import MicrophonePermissionHandler from "../MicrophonePermissionHandler";
import softphoneService from "../../app/components/SoftphoneController/index.js";
import CallDncLitigator from "../../app/AccountNew/modals/CallDncLitigator.js";
import dnccss from "../../app/AccountNew/style.scss";
import ToastNotification from "../../app/components/ToastNotification";
import AdminSetup from "../../app/AccountNew/modals/AdminSetup.js";
import NiceWorkModal from "../../app/AccountNew/modals/NiceWorkModal.js";
import { MAX_DAILY_CALLS } from "../../data/user/constants.js";

const ClickToDial = ({ profile }) => {
  const dispatch = useDispatch();

  const userId = useSelector(selectUserId);
  const userProfile = useSelector(selectUserProfile)?.toJS();
  const isClickToDialOpen = useSelector(selectIsClickToDialOpen);
  const leadAutomator = useSelector(selectLeadAutomator)?.toJS();
  const callSettings = useSelector(selectCallSettings);
  const contactPropertyDetails = useSelector(selectClickToDialContactPropertyDetails)?.toJS();
  const clickToDialData = useSelector(selectClickToDialData);
  const isCallSettingsLoading = useSelector(selectCallSettingsLoading);
  const isLeadAutomatorLoading = useSelector(selectIsLeadAutomatorLoading);
  const alert = useSelector(selectAlert)?.toJS();
  const alertRef = useRef(null);
  const contactPropertyDetailsRef = useRef(null);

  const microphonePermissionHandler = new MicrophonePermissionHandler();

  const callCountForToday = useSelector(selectCallCountForToday);
  const callCountForTodayLoading = useSelector(selectCallCountForTodayLoading);

  const [isOpen, setIsOpen] = useState(false);
  const [showReadyToCall, setShowReadyToCall] = useState(false);
  const [isMicrophoneNotDetectedModalOpen, setIsMicrophoneNotDetectedModalOpen] = useState(false);
  const isCallStartingRef = useRef(false);

  const [isNiceWorkModalOpen, setIsNiceWorkModalOpen] = useState(false);
  const [isNiceWorkModalDoNotShowAgainChecked, setIsNiceWorkModalDoNotShowAgainChecked] = useState(false);
  const callCountForTodayRef = useRef(0);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const [isProceeding, setIsProceeding] = useState(false);

  const hasLeadAutomator =
    leadAutomator?.accountPlanMrcs &&
    leadAutomator.accountPlanMrcs.length > 0;

  const isFullAccessTeamMember = (() => {
    if (profile) {
      if (profile.get && (typeof profile.get == "function")) {
        return profile.get("subUser") && !profile.get("scoutUser");
      }
      if ("subUser" in profile && "scoutUser" in profile) {
        return profile.subUser && !profile.scoutUser;
      }
    }
    return undefined;
  })();

  const isLegacyTeamMember = (() => {
    if (profile) {
      if (profile.get && (typeof profile.get == "function")) {
        return profile.get("type") === "TEAM";
      }
      if ("type" in profile) {
        return profile.type === "TEAM";
      }
    }
    return undefined;
  })();

  const hasPermission = (() => {
    if (profile) {
      if (profile.get && (typeof profile.get == "function")) {
        return profile.get("allowClickToDial");
      }
      if ("allowClickToDial" in profile) {
        return profile.allowClickToDial;
      }
    }
    return undefined;
  })();

  const outboundNumber = callSettings?.[0]?.outboundNumber;
  const [complianceType, setComplianceType] = useState(null);

  const buildAgentInfo = () => ({
    id: userProfile.userId,
    firstname: userProfile.firstName,
    lastname: userProfile.lastName,
    meta: { email: userProfile.email },
  });
  useEffect(() => {
    if (!isClickToDialOpen) return;
    if (!hasLeadAutomator) return;
    softphoneService.prewarmMicPermission(microphonePermissionHandler);
  }, [isClickToDialOpen, hasLeadAutomator]);

  useEffect(() => {
    if (!isClickToDialOpen) return;
    if (!hasLeadAutomator || !outboundNumber) return;
    if (!userProfile || Object.keys(userProfile).length === 0) return;

    softphoneService.prewarmRegistration(
      buildAgentInfo(),
      phoneNumberToOnlyNumeric(outboundNumber)
    );
  }, [isClickToDialOpen, hasLeadAutomator, outboundNumber, userProfile]);

  useEffect(() => {
    softphoneService.registerOnCleanup(() => {
      if (!userProfile || Object.keys(userProfile).length === 0 || !outboundNumber) return;

      softphoneService.prewarmRegistration(
        buildAgentInfo(),
        phoneNumberToOnlyNumeric(outboundNumber)
      );
    });

    return () => softphoneService.registerOnCleanup(null);
  }, [userProfile, outboundNumber]);

  useEffect(() => {
    if (userId) {
      dispatch(clickToDialIsLoaded(false));

      dispatch(getLeadAutomatorAPI(userId));
      dispatch(getCallSettingIntegration(userId));
      dispatch(getUserProfile(userId));
      dispatch(getAlertByName(DailyCallLimit_NiceWorkAlert_Name));
    }
  }, [userId, dispatch]);

  // Check if all ClickToDial dependencies are loaded
  // Or allow the legacy team member, to see the Full Access Required modal
  useEffect(() => {
    if (isLegacyTeamMember || (!isLeadAutomatorLoading && !isCallSettingsLoading && (userProfile && Object.keys(userProfile).length))) {
      dispatch(clickToDialIsLoaded(true));
    }
  }, [isLeadAutomatorLoading, isCallSettingsLoading, userProfile, dispatch]);

  const handleClickToDialDataChanged = async () => {
    if (clickToDialData?.contactId) {
      dispatch(getClickToDialContactPropertyDetailsById(clickToDialData.contactId));

      if (!window.isGetCallCountForTodayLoading) {
        window.isGetCallCountForTodayLoading = true;
        await dispatch(getCallCountForToday());
        window.isGetCallCountForTodayLoading = null;
      }
    }
  }

  useEffect(() => {
    handleClickToDialDataChanged(clickToDialData);
  }, [clickToDialData, dispatch]);

  useEffect(() => {
    setIsOpen(isClickToDialOpen);

    isCallStartingRef.current = false;
  }, [isClickToDialOpen]);

  useEffect(() => {
    setShowReadyToCall(Boolean(outboundNumber));
  }, [callSettings, outboundNumber]);

  useEffect(() => {
    callCountForTodayRef.current = callCountForToday;
  }, [callCountForToday]);

  useEffect(() => {
    alertRef.current = alert;
  }, [alert]);

  useEffect(() => {
    contactPropertyDetailsRef.current = contactPropertyDetails;
  }, [contactPropertyDetails]);

  const showToastErrorMessage = () => {
    setToastMessage("Something went wrong. Please try again.");
    setToastType("error");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }

  const {
    marketingCampaignId,
    dialerCampaignId,
    accountId,
  } = clickToDialData || {};

  const phoneNumberToOnlyNumeric = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length == 0) {
      return phoneNumber;
    }
    return phoneNumber.split("").filter((item) => !isNaN(item) && item.trim() != "").join("");
  }

  const handleCallEnded = useCallback(async () => {
    try {
      await logCall(phoneNumberToOnlyNumeric(contactPropertyDetailsRef.current.batchDialerData.outboundNumber));
    }
    catch (e) {
      // swallow — logging failure shouldn't block the end-of-call UX
    }

    if (callCountForTodayRef.current === DailyCallLimit_Notification_CallCountThreshold && alertRef.current?.id != null) {
      setIsNiceWorkModalOpen(true);
      dispatch(openClickToDial(true));
    }
  }, [dispatch]);

  const registerToSoftphone = async () => {
    if (!contactPropertyDetails?.batchDialerData?.outboundNumber) {
      return;
    }

    await softphoneService.ensureRegistered(
      buildAgentInfo(),
      phoneNumberToOnlyNumeric(contactPropertyDetails.batchDialerData.outboundNumber)
    );
  };

  const handleCall = async () => {
    if (!clickToDialData) return;

    const { myDnc, publicDnc, litigatorFlag } = clickToDialData;

    const isDNC = myDnc === true || publicDnc === true;
    const isLitigator = litigatorFlag === true;

    if (isDNC && isLitigator) {
      setComplianceType("both");
      return;
    }

    if (isDNC) {
      setComplianceType("dnc");
      return;
    }

    if (isLitigator) {
      setComplianceType("litigator");
      return;
    }

    await handleMicrophonePermissionTry();
  };

  const handleMicrophonePermissionTry = async () => {
    try {
      if (!softphoneService.micStream) {
        const response = await microphonePermissionHandler.getPermission();

        if (response.error) {
          setIsMicrophoneNotDetectedModalOpen(true);
          return;
        }

        softphoneService.micStream = response.stream;
        setIsMicrophoneNotDetectedModalOpen(false);
      }

      await startCall();
    } finally {
      isCallStartingRef.current = false;
    }
  };

  const logCall = async (outboundNumber) => {
    await dispatch(saveCallLog({outboundNumber}));
  }

  const startCall = async () => {
    if ((!userProfile || Object.keys(userProfile).length == 0) || !contactPropertyDetails) {
      return;
    }

    try {
      dispatch(openClickToDial(false));

      if (!contactPropertyDetails.contactInfo) {
        contactPropertyDetails.contactInfo = {};
      }
      if (!contactPropertyDetails.propertyInfo) {
        contactPropertyDetails.propertyInfo = {};
      }

      contactPropertyDetails.contactInfo.phonenumber = phoneNumberToOnlyNumeric(clickToDialData.phonenumber);
      contactPropertyDetails.contactInfo.vendorcontactid = contactPropertyDetails.contactInfo.contactId;
      contactPropertyDetails.contactInfo.firstname = contactPropertyDetails.contactInfo.firstName;
      contactPropertyDetails.contactInfo.lastname = contactPropertyDetails.contactInfo.lastName;
      contactPropertyDetails.contactInfo.addressline1 = contactPropertyDetails.contactInfo.streetAddress;
      contactPropertyDetails.contactInfo.postalcode = contactPropertyDetails.contactInfo.zip;
      contactPropertyDetails.contactInfo.country = "US";

      contactPropertyDetails.propertyInfo.img = contactPropertyDetails.propertyInfo.imageUrl;
      contactPropertyDetails.propertyInfo.address = contactPropertyDetails.propertyInfo.address?.title;

      const registerOnCallEndedEventHandler = () => {
        softphoneService.registerOnCallEnded(handleCallEnded);
      };

      const checkRegistrationAndRegister = async () => {
        if (!softphoneService.isRegistered) {
          await registerToSoftphone();
        }
      };

      const call = async () => {
        await softphoneService.dial({
          contact: contactPropertyDetails.contactInfo, propertyInfo: contactPropertyDetails.propertyInfo, userId: userProfile.userId,
          marketingCampaignId,
          dialerCampaignId,
          accountId,
        });
      };

      await checkRegistrationAndRegister();
      registerOnCallEndedEventHandler();
      await call();
    } catch (error) {
      showToastErrorMessage();
    }
  }

  if (!isOpen) return (
    <ToastNotification
      message={toastMessage}
      show={showToast}
      type={toastType}
    />
  );

  if (isLegacyTeamMember) {
    return <FullAccessRequiredModal
      onClose={() => dispatch(openClickToDial(false))}
    />;
  }

  if (!hasLeadAutomator && isFullAccessTeamMember) {
    return <AdminSetup
      isOpen={true}
      onClose={() => dispatch(openClickToDial(false))}
      onSuccess={() => dispatch(openClickToDial(false))}
    />;
  }

  if (hasLeadAutomator && isFullAccessTeamMember && !outboundNumber) {
    return <AdminSetup
      isOpen={true}
      onClose={() => dispatch(openClickToDial(false))}
      onSuccess={() => dispatch(openClickToDial(false))}
      content={"To use this feature, your account admin must first\nset up a phone number. Please contact your admin\nfor assistance."}
    />;
  }

  if (hasLeadAutomator && isFullAccessTeamMember && outboundNumber && !hasPermission) {
    return <AdminSetup
      isOpen={true}
      onClose={() => dispatch(openClickToDial(false))}
      onSuccess={() => dispatch(openClickToDial(false))}
      content={"To use this feature, your account admin must first\nenable access for your account. Please contact\nyour admin for assistance."}
    />;
  }

  if (!hasLeadAutomator) {
    return <UpgradeToLeadAutomator />;
  }

  if (isNiceWorkModalOpen) {
    return <NiceWorkModal
      isOpen={true}
      onClose={() => {
        if (isNiceWorkModalDoNotShowAgainChecked) {
          dispatch(disableAlert(alert.id, () => {
            dispatch(getAlertByName(DailyCallLimit_NiceWorkAlert_Name));
          }));
          setIsNiceWorkModalDoNotShowAgainChecked(false);
        }

        dispatch(openClickToDial(false));
        setIsNiceWorkModalOpen(false);
      }}
      isDoNotShowAgainChecked={isNiceWorkModalDoNotShowAgainChecked}
      onDoNotShowAgainCheck={(isChecked) => setIsNiceWorkModalDoNotShowAgainChecked(isChecked)}
    />;
  }

  if (!showReadyToCall) {
    return (
      <div className={css.clsNewCallSettingSection}>
        <SetupPhoneNumber
          isOpen={isClickToDialOpen}
          onClose={() => { }}
          onCancel={() => dispatch(openClickToDial(false))}
        />
      </div>
    );
  }

  if (isMicrophoneNotDetectedModalOpen) {
    return (
      <MicrophoneNotDetected
        onRetry={handleMicrophonePermissionTry}
        onIgnore={() => {
          setIsMicrophoneNotDetectedModalOpen(false);
          dispatch(openClickToDial(false));
        }}
      />
    );
  }

  const getFullName = () => {
    if (contactPropertyDetails) {
      let fullNameArray = [];
      if (contactPropertyDetails.contactInfo?.firstName && contactPropertyDetails.contactInfo?.firstName.length > 0) {
        fullNameArray.push(contactPropertyDetails.contactInfo.firstName);
      }
      if (contactPropertyDetails.contactInfo?.lastName && contactPropertyDetails.contactInfo?.lastName.length > 0) {
        fullNameArray.push(contactPropertyDetails.contactInfo.lastName);
      }
      return fullNameArray.join(" ");
    }
    return "";
  }

  const getInitials = () => {
    const info = contactPropertyDetails?.contactInfo;
    if (!info) return "";

    const fullName = [info.firstName, info.lastName].filter(Boolean).join(" ");
    if (!fullName) return "";

    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 1) {
      return parts[0][0].toUpperCase();
    }

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const formatPhoneNumber = (phoneNumber) => {
    return phoneNumber && phoneNumber.length == 10 ? `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, phoneNumber.length)}` : phoneNumber;
  }

  const findContactPhone = (phoneList) => {
    return phoneList?.find((item) => phoneNumberToOnlyNumeric(item.phone) == phoneNumberToOnlyNumeric(clickToDialData?.phonenumber))
  }


  return (
    <>
      {!complianceType && !isProceeding && (
        <div className={readytocallscss.overlay} onClick={() => dispatch(openClickToDial(false))}>
          <div
            className={readytocallscss.center}
            onClick={(e) => e.stopPropagation()}
          >
            <ReadyToCall
              name={getFullName() || clickToDialData?.name}
              phone={formatPhoneNumber(clickToDialData?.phonenumber)}
              initials={getInitials() || clickToDialData?.intials}
              litigatorFlag={contactPropertyDetails?.contactInfo?.litigatorFlag}
              myDnc={findContactPhone(contactPropertyDetails?.contactInfo?.phones)?.myDNC}
              publicDnc={findContactPhone(contactPropertyDetails?.contactInfo?.phones)?.publicDNC}
              onCancel={() => dispatch(openClickToDial(false))}
              onCallNow={async () => {
                if (isCallStartingRef.current) return;
                isCallStartingRef.current = true;
                try {
                  await handleCall();
                } catch (error) {
                  isCallStartingRef.current = false;
                }
              }}
              callCountForToday={callCountForToday}
              callCountForTodayLoading={callCountForTodayLoading}
              maxCalls={MAX_DAILY_CALLS}
            />
          </div>
        </div>
      )}
      {complianceType && (
        <div className={dnccss.clsIntegrationCard}>
          <CallDncLitigator
            isOpen={true}
            complianceType={complianceType}
            onClose={() => {
              setIsProceeding(false);
              setComplianceType(null);
              dispatch(openClickToDial(false));
              isCallStartingRef.current = false;
            }}
            onSuccess={async () => {
              setIsProceeding(true);
              setComplianceType(null);
              dispatch(openClickToDial(false));
              try {
                await handleMicrophonePermissionTry();
              } finally {
                isCallStartingRef.current = false;
                setIsProceeding(false);
              }
            }}
          />
        </div>
      )}
      <ToastNotification
        message={toastMessage}
        show={showToast}
        type={toastType}
      />
    </>
  );
};

export default ClickToDial;